import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { 
  CreditCard, 
  Calendar, 
  Users, 
  FileText, 
  CheckCircle, 
  Clock,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const BillingDashboard = () => {
  const [billingInfo, setBillingInfo] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);

  const { API } = useContext(AuthContext);

  useEffect(() => {
    fetchBillingInfo();
    fetchPlans();
  }, []);

  const fetchBillingInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/billing/info`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBillingInfo(data);
      } else {
        console.error('Failed to fetch billing info');
      }
    } catch (error) {
      console.error('Error fetching billing info:', error);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API}/billing/plans`);
      if (response.ok) {
        const data = await response.json();
        setPlans(data);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    setUpgrading(true);
    try {
      const token = localStorage.getItem('token');
      const currentUrl = window.location.origin;
      
      const response = await fetch(`${API}/billing/checkout/session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          plan_id: planId,
          success_url: `${currentUrl}/billing/success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${currentUrl}/billing`
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create checkout session");
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error("Failed to initiate upgrade process");
    } finally {
      setUpgrading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'trial':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800">Trial</Badge>;
      case 'active':
        return <Badge variant="default" className="bg-green-100 text-green-800">Active</Badge>;
      case 'past_due':
        return <Badge variant="destructive">Past Due</Badge>;
      case 'canceled':
        return <Badge variant="outline">Canceled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Billing & Subscription</h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">Manage your department's subscription and billing</p>
        </div>
      </div>

      {/* Current Subscription Status */}
      {billingInfo && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Current Subscription
                </CardTitle>
                <CardDescription>Your current plan and usage</CardDescription>
              </div>
              {getStatusBadge(billingInfo.subscription_status)}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              <div className="space-y-2">
                <div className="text-sm text-gray-600">Current Plan</div>
                <div className="text-base sm:text-lg font-semibold">
                  {billingInfo.current_plan?.name || 'No Plan'}
                </div>
                <div className="text-xl sm:text-2xl font-bold text-blue-600">
                  ${billingInfo.current_plan?.price || 0}/month
                </div>
              </div>

              {billingInfo.trial_days_remaining !== null && (
                <div className="space-y-2">
                  <div className="text-sm text-gray-600 flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    Trial Period
                  </div>
                  <div className="text-lg font-semibold">
                    {billingInfo.trial_days_remaining} days remaining
                  </div>
                  {billingInfo.trial_days_remaining <= 7 && (
                    <div className="flex items-center gap-1 text-amber-600">
                      <AlertTriangle className="h-4 w-4" />
                      <span className="text-sm">Trial ending soon</span>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <div className="text-sm text-gray-600 flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Next Billing Date
                </div>
                <div className="text-lg font-semibold">
                  {billingInfo.next_billing_date 
                    ? new Date(billingInfo.next_billing_date).toLocaleDateString()
                    : 'N/A'
                  }
                </div>
              </div>
            </div>

            {/* Usage Stats */}
            {billingInfo.usage_stats && (
              <div className="mt-6 pt-6 border-t">
                <h3 className="text-lg font-semibold mb-4">Usage This Month</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-blue-600" />
                      <div>
                        <div className="font-medium">Requests</div>
                        <div className="text-sm text-gray-600">
                          {billingInfo.usage_stats.requests_this_month} / {
                            billingInfo.usage_stats.plan_limit_requests || 'Unlimited'
                          }
                        </div>
                      </div>
                    </div>
                    <div className="text-2xl font-bold">
                      {billingInfo.usage_stats.requests_this_month}
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-green-600" />
                      <div>
                        <div className="font-medium">Staff Users</div>
                        <div className="text-sm text-gray-600">
                          {billingInfo.usage_stats.total_staff} / {
                            billingInfo.usage_stats.plan_limit_staff || 'Unlimited'
                          }
                        </div>
                      </div>
                    </div>
                    <div className="text-2xl font-bold">
                      {billingInfo.usage_stats.total_staff}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Available Plans */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Available Plans</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => {
            const isCurrentPlan = billingInfo?.current_plan?.name === plan.name;
            
            return (
              <Card key={plan.id} className={`relative ${isCurrentPlan ? 'ring-2 ring-blue-500' : ''}`}>
                {isCurrentPlan && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-blue-500 text-white">Current Plan</Badge>
                  </div>
                )}
                
                <CardHeader>
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <div className="text-3xl font-bold text-blue-600">
                    ${plan.price}
                    <span className="text-sm font-normal text-gray-600">/month</span>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {!isCurrentPlan && (
                    <Button 
                      onClick={() => handleUpgrade(plan.id)}
                      disabled={upgrading}
                      className="w-full"
                    >
                      {upgrading ? 'Processing...' : `Upgrade to ${plan.name}`}
                    </Button>
                  )}

                  {isCurrentPlan && billingInfo?.subscription_status === 'trial' && (
                    <Button 
                      onClick={() => handleUpgrade(plan.id)}
                      disabled={upgrading}
                      className="w-full"
                      variant="outline"
                    >
                      {upgrading ? 'Processing...' : 'Activate Subscription'}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Trial Warning */}
      {billingInfo?.subscription_status === 'trial' && billingInfo?.trial_days_remaining <= 7 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
              <div>
                <h3 className="font-semibold text-amber-900">Trial Ending Soon</h3>
                <p className="text-amber-800">
                  Your trial expires in {billingInfo.trial_days_remaining} days. 
                  Upgrade now to continue using all features.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BillingDashboard;